import { buildToggleOptions } from '@/screens/ReportCreation/forms/form.utils';
import type { FormLayout } from '@/screens/ReportCreation/reportCreation.types';
import type { TFunction } from 'i18next';
import { SupportedLanguage } from '@store/i18n';

/**
 * Step 1: General Information - Form Configuration
 */

export const getStep1FormConfig = (
  t: TFunction,
  language: SupportedLanguage,
): FormLayout => {
  const animalTypeOptions = buildToggleOptions(
    t,
    [
      {
        value: 'vertebrate',
        labelKey: 'reportCreation.step1.options.animalType.vertebrate',
      },
      {
        value: 'invertebrate',
        labelKey: 'reportCreation.step1.options.animalType.invertebrate',
      },
    ],
    language,
  );

  const wildtypeOptions = buildToggleOptions(
    t,
    [
      {
        value: 'freeRoaming',
        labelKey: 'reportCreation.step1.options.wildtype.freeRoaming',
      },
      {
        value: 'captive',
        labelKey: 'reportCreation.step1.options.wildtype.captive',
      },
      {
        value: 'feral',
        labelKey: 'reportCreation.step1.options.wildtype.feral',
      },
    ],
    language,
  );

  return {
    sections: [
      {
        title: t('reportCreation.step1.sections.animalDescription', { lng: language }),
        fields: [
          {
            id: 'animalType',
            type: 'toggle-group',
            label: t('reportCreation.step1.fields.animalType', { lng: language }),
            required: true,
            options: animalTypeOptions,
          },
          {
            id: 'species',
            type: 'species-search',
            label: t('reportCreation.step1.fields.species', { lng: language }),
            placeholder: t('reportCreation.step1.placeholders.selectSpecies', { lng: language }),
            required: true,
          },
          {
            id: 'wildtype',
            type: 'toggle-group',
            label: t('reportCreation.step1.fields.wildtype', { lng: language }),
            required: true,
            options: wildtypeOptions,
          },
        ],
      },
      {
        title: t('reportCreation.step1.sections.location', { lng: language }),
        fields: [
          {
            id: 'location',
            type: 'location',
            required: true,
            latitudePlaceholder: t(
              'reportCreation.step1.placeholders.latitude',
              { lng: language },
            ),
            longitudePlaceholder: t(
              'reportCreation.step1.placeholders.longitude',
              { lng: language },
            ),
          },
          {
            id: 'inaccurateLocation',
            type: 'checkbox',
            label: t('reportCreation.step1.fields.inaccurateLocation', { lng: language }),
            required: false,
          },
        ],
      },
      {
        title: t('reportCreation.step1.sections.dateOfDetection', { lng: language }),
        fields: [
          {
            id: 'dateOfDetection',
            type: 'date',
            placeholder: t('reportCreation.step1.placeholders.dateFormat', { lng: language }),
            required: true,
          },
        ],
      },
    ],
  };
};
